import { useParams } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';
import {
  useClientDossier,
  useClientFrise,
  useClientDossierFiles,
  useClientFileDownload,
} from '../../hooks/queries/useClientDossiers';
import { formatFileSize } from '../../services/files/fileUtils';
import { formatStageDate } from '../../services/dossiers/stageDate';

export function PortalDossierDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: dossier, isLoading } = useClientDossier(id);
  const { data: steps = [] } = useClientFrise(id);
  const { data: files = [] } = useClientDossierFiles(id);
  const download = useClientFileDownload();

  async function handleDownload(fileId: string) {
    try {
      const url = await download.mutateAsync(fileId);
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.target = '_blank';
      a.click();
    } catch {
      alert('Téléchargement impossible, réessayez.');
    }
  }

  if (isLoading) {
    return <p className="text-sm text-cime-text-muted">Chargement…</p>;
  }

  if (!dossier) {
    return (
      <div className="text-center py-12">
        <p className="text-lg font-semibold text-neutral-900">Dossier introuvable</p>
        <p className="text-sm text-cime-text-muted mt-1">
          Ce dossier n&apos;existe pas ou ne vous est pas accessible.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div>
        <p className="text-xs text-cime-text-muted">{dossier.number}</p>
        <h1
          className="text-xl font-semibold text-cime-text-primary"
          style={{ fontFamily: 'var(--cime-font-heading)' }}
        >
          {dossier.title}
        </h1>
      </div>

      {/* Suivi du dossier */}
      <section className="bg-cime-surface border border-neutral-200 rounded-cime p-4">
        <h2 className="text-sm font-semibold text-cime-text-primary mb-3">
          Suivi du dossier
        </h2>
        <ol className="space-y-1.5">
          {steps.map((step) => (
            <li key={step.position} className="text-sm flex items-baseline gap-2">
              <span className={step.is_current ? 'font-semibold text-cime-text-primary' : 'text-neutral-500'}>
                {step.label}
              </span>
              {step.effective_on && (
                <span className="text-cime-text-muted">{formatStageDate(step.effective_on)}</span>
              )}
              {step.is_current && (
                <span className="text-[11px] uppercase tracking-wide text-cime-primary">en cours</span>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* Prochaine audience */}
      {dossier.next_hearing_at && (
        <section className="bg-cime-surface border border-neutral-200 rounded-cime p-4">
          <h2 className="text-sm font-semibold text-cime-text-primary mb-1">
            Prochaine audience
          </h2>
          <p className="text-sm">
            {new Date(dossier.next_hearing_at).toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </section>
      )}

      {/* Votre avocat */}
      <section className="bg-cime-surface border border-neutral-200 rounded-cime p-4">
        <h2 className="text-sm font-semibold text-cime-text-primary mb-2">
          Votre avocat
        </h2>
        <p className="text-sm">
          {dossier.lead_counsel_name ?? '—'}
          {dossier.lead_counsel_email && (
            <>
              {' · '}
              <a
                href={`mailto:${dossier.lead_counsel_email}`}
                className="text-cime-primary hover:underline"
              >
                {dossier.lead_counsel_email}
              </a>
            </>
          )}
        </p>
      </section>

      {/* Documents partagés */}
      <section className="bg-cime-surface border border-neutral-200 rounded-cime p-4">
        <h2 className="text-sm font-semibold text-cime-text-primary mb-2">
          Documents partagés
        </h2>
        {files.length === 0 ? (
          <p className="text-sm text-cime-text-muted">
            Aucun document partagé pour l&apos;instant.
          </p>
        ) : (
          <div className="space-y-1">
            {files.map((f) => (
              <div
                key={f.id}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-neutral-200 bg-neutral-50"
              >
                <FileText className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                <span className="flex-1 min-w-0 text-sm truncate">{f.filename}</span>
                <span className="text-xs text-neutral-400">
                  {f.size != null ? formatFileSize(f.size) : ''}
                </span>
                <button
                  type="button"
                  onClick={() => f.id && handleDownload(f.id)}
                  className="p-1 rounded hover:bg-neutral-200"
                  aria-label={`Télécharger ${f.filename ?? 'ce fichier'}`}
                >
                  <Download className="w-3.5 h-3.5 text-neutral-600" />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
